import { Box, Stack, Typography } from '@mui/material';

import {
  IconButton,
  IconButtonProps,
} from '@/components/form/baseInputs/Button/IconButton';
import { RefreshCw05Icon } from '@/components/icons/RefreshCw05Icon';
import { Card } from '@/components/layout/Card/Card';
import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { Tooltip } from '@/components/poppers/Tooltip/Tooltip';
import { Loader } from '@/components/progress/Loader/Loader';
import { getUserFacingErrorMessages } from '@/graphql/errors';
import { getPublicImageUrl } from '@/utils/staticFiles';

import {
  AdminIntegrationsPage_IntegrationConfigurationFragment,
  useSyncSnowflakeAssetsMutation,
  useSyncSnowflakeHouseholdsMutation,
} from '../graphql/AdminIntegrationsPage.generated';
import { SnowflakeSyncToggle } from './components/SnowflakeSyncToggle';
import { useSnowflakeSyncToggle } from './hooks/useSnowflakeSyncToggle';

interface SnowflakeSyncButtonProps extends Partial<IconButtonProps> {
  label: string;
  loading: boolean;
  onClick: () => void;
}

function SnowflakeSyncButton({
  label,
  loading,
  onClick,
  ...buttonProps
}: SnowflakeSyncButtonProps) {
  return (
    <Tooltip title={label}>
      <IconButton
        icon={RefreshCw05Icon}
        ariaLabel={label}
        size="xs"
        loading={loading}
        onClick={onClick}
        {...buttonProps}
      />
    </Tooltip>
  );
}

export interface SnowflakeIntegrationCardProps {
  configuration: AdminIntegrationsPage_IntegrationConfigurationFragment | null;
  disabled?: boolean;
}

export function SnowflakeIntegrationCard({
  configuration,
  disabled = false,
}: SnowflakeIntegrationCardProps) {
  const { showFeedback } = useFeedback();
  const {
    enabled: isDataSharingEnabled,
    isUpdating: isUpdatingDataSharing,
    toggle: toggleDataSharing,
  } = useSnowflakeSyncToggle({
    configuration,
    field: 'snowflakeDataSharingEnabled',
    description: 'Snowflake asset sync',
  });
  const {
    enabled: isHouseholdSyncEnabled,
    isUpdating: isUpdatingHouseholdSync,
    toggle: toggleHouseholdSync,
  } = useSnowflakeSyncToggle({
    configuration,
    field: 'snowflakeHouseholdSyncEnabled',
    description: 'Snowflake household sync',
  });
  const [syncAssets, { loading: isSyncingAssets }] =
    useSyncSnowflakeAssetsMutation({
      onCompleted: () => {
        showFeedback('Snowflake asset sync started', { variant: 'success' });
      },
      onError: (error) => {
        showFeedback(
          `Failed to start Snowflake asset sync: ${getUserFacingErrorMessages(error).join(' ')}`
        );
      },
    });
  const [syncHouseholds, { loading: isSyncingHouseholds }] =
    useSyncSnowflakeHouseholdsMutation({
      onCompleted: () => {
        showFeedback('Snowflake household sync started', {
          variant: 'success',
        });
      },
      onError: (error) => {
        showFeedback(
          `Failed to start Snowflake household sync: ${getUserFacingErrorMessages(error).join(' ')}`
        );
      },
    });

  if (!configuration) {
    return <Loader />;
  }

  return (
    <Stack>
      <Box textAlign="center">
        <img
          alt="Snowflake"
          src={getPublicImageUrl('/snowflakeLogo.svg')}
          height={28}
        />
      </Box>
      <Stack mb={2} mt={1} spacing={2}>
        <Typography variant="body-s">
          Configure Snowflake data sharing for assets and households.
        </Typography>
        <Card variant="filled" sx={{ p: 2 }}>
          <Stack spacing={2}>
            <SnowflakeSyncToggle
              label="Assets sync"
              enabled={isDataSharingEnabled}
              onChange={() => {
                void toggleDataSharing();
              }}
              disabled={disabled || isUpdatingDataSharing}
              syncButton={
                <SnowflakeSyncButton
                  label="Sync Snowflake assets"
                  loading={isSyncingAssets}
                  disabled={disabled}
                  onClick={() => {
                    void syncAssets();
                  }}
                />
              }
            />
            <SnowflakeSyncToggle
              label="Households sync"
              enabled={isHouseholdSyncEnabled}
              onChange={() => {
                void toggleHouseholdSync();
              }}
              disabled={disabled || isUpdatingHouseholdSync}
              syncButton={
                <SnowflakeSyncButton
                  label="Sync Snowflake households"
                  loading={isSyncingHouseholds}
                  disabled={disabled}
                  onClick={() => {
                    void syncHouseholds();
                  }}
                />
              }
            />
          </Stack>
        </Card>
      </Stack>
    </Stack>
  );
}
